/**
 * The workbench's filter panel.
 *
 * FACET COUNTS BESIDE EVERY OPTION, which is the whole reason this is not a
 * row of chips. The point of a filter panel over a search box is that you can
 * see what is there before you click: «مشترک (۱۲)» tells you whether the next
 * click is worth making, and an option showing (۰) tells you not to bother.
 *
 * The specialty list is FLAT with indentation rather than a collapsible tree.
 * Picking a parent already includes its children server-side, so a tree would
 * add a disclosure control that changes nothing about the answer.
 */
import { Badge, Button } from '@meshhub/design-tokens/ui';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  CONTACT_KIND_LABELS,
  GENDER_LABELS,
  formatCount,
} from '../../lib/format.js';
import { CONTACTS_QUERY } from '../../lib/queries.js';
import { DENSITY } from '../../constants/density.js';
import { EMPTY_FILTER, countActiveFilters } from '../../lib/filters.js';
import { S } from '../../lib/strings.js';

const KINDS = ['PERSON', 'ORGANIZATION'];
/**
 * OTHER is offered alongside مرد and زن.
 *
 * The request named two options; the model has had three since Step 1 and the
 * contact form has always offered all three. Leaving OTHER out would make
 * every contact recorded that way unreachable through this filter — a filter
 * that hides rows is worse than one option more than was asked for.
 */
const GENDERS = ['MALE', 'FEMALE', 'OTHER'];

/**
 * The six vocabularies this panel filters on that the contact form also uses.
 *
 * Title comes from `S.search`, options from `lookups[kind]` — so nothing here
 * names a value, and a deployment that renames «دمو انجام شده» renames the
 * filter option with it (CLAUDE.md §1).
 */
const VOCABULARY_SECTIONS = [
  ['LIFECYCLE_STAGE', 'lifecycleStageIds', 'lifecycleStage'],
  ['SALES_STAGE', 'salesStageIds', 'salesStage'],
  ['CUSTOMER_HEALTH', 'customerHealthIds', 'customerHealth'],
  ['ENGAGEMENT_STATUS', 'engagementStatusIds', 'engagementStatus'],
  ['FACILITY_TYPE', 'facilityTypeIds', 'facilityType'],
  ['CITY', 'cityIds', 'city'],
];

/** Facet lookup: kind + key -> count, built once per render. */
function facetIndex(facets) {
  const index = new Map();
  for (const facet of facets ?? []) index.set(`${facet.kind}:${facet.key}`, facet.count);
  return index;
}

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-b border-border pb-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between py-2 text-sm font-medium text-text"
      >
        {title}
        {open ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
      </button>
      {/* A long vocabulary scrolls INSIDE its section rather than pushing the
          rest of the panel off the screen — «شهر» alone has 31 options, and a
          panel of twelve filters that reads as a panel of four is not a panel.
          The cap is a density decision, so it comes from the one module
          allowed to hold one. */}
      {open && (
        <div
          className="flex flex-col gap-1 overflow-y-auto"
          style={{ maxHeight: DENSITY.filterSectionMax }}
        >
          {children}
        </div>
      )}
    </section>
  );
}

/**
 * «معرف» — pick one contact and see everyone they introduced.
 *
 * A search box narrowing a `<select>`, which is the shape every other contact
 * picker in this app already has (the affiliation dialog, the referrer field
 * on the contact form, the activity dialog). A fourth way to choose a contact
 * would be a fourth thing to learn.
 *
 * ONE, not a list: «who did رضا bring in» is asked about one person at a time,
 * and the filter reads as a sentence that way.
 */
function ReferrerFilter({ value, onChange }) {
  const [search, setSearch] = useState('');
  const { data, loading } = useQuery(CONTACTS_QUERY, {
    variables: { page: 1, pageSize: 25, search: search.trim() || null },
    errorPolicy: 'ignore',
  });
  const candidates = data?.contacts?.items ?? [];
  // The current selection stays listed even when the search has moved past it,
  // or narrowing the box would silently clear the filter.
  const chosen = candidates.find((c) => c.id === value);

  return (
    <div className="flex flex-col gap-2 px-2 pb-1">
      <input
        type="text"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={S.contacts.detail.searchContact}
        aria-label={S.contacts.detail.searchContact}
        className="h-control rounded border border-border bg-bg px-3 text-sm outline-none
                   focus:border-primary"
      />
      <select
        value={value ?? ''}
        aria-label={S.search.referrer}
        onChange={(event) => onChange(event.target.value)}
        className="h-control rounded border border-border bg-bg px-3 text-sm outline-none
                   focus:border-primary"
      >
        <option value="">{loading ? S.app.loading : S.search.referrerAny}</option>
        {value && !chosen && <option value={value}>{S.app.none}</option>}
        {candidates.map((contact) => (
          <option key={contact.id} value={contact.id}>
            {contact.displayName}
          </option>
        ))}
      </select>
    </div>
  );
}

function CheckOption({ label, count, checked, onChange, indent = 0 }) {
  return (
    <label
      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm
                 hover:bg-surface-hover"
      style={{ paddingInlineStart: `calc(${indent} * var(--mh-space-4))` }}
    >
      <input type="checkbox" className="accent-primary" checked={checked} onChange={onChange} />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && (
        <span className="shrink-0 text-xs text-text-muted tabular-nums">
          ({formatCount(count)})
        </span>
      )}
    </label>
  );
}

export function FilterPanel({
  filter,
  onChange,
  facets,
  tags = [],
  lookups = {},
  searchRef,
}) {
  const counts = facetIndex(facets);
  const assessments = tags.filter((tag) => tag.isAssessment);
  const set = (patch) => onChange({ ...filter, ...patch });
  const toggle = (key, value) => {
    const current = filter[key] ?? [];
    set({
      [key]: current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value],
    });
  };
  const active = countActiveFilters(filter);

  return (
    <aside
      className="flex w-filters shrink-0 flex-col gap-1 overflow-y-auto border-e border-border
                 bg-surface p-4"
      aria-label={S.search.filters}
    >
      <div className="flex items-center justify-between pb-2">
        <h2 className="text-sm font-bold">
          {S.search.filters}
          {active > 0 && (
            <Badge className="ms-2 bg-primary text-primary-contrast">
              {formatCount(active)}
            </Badge>
          )}
        </h2>
        {active > 0 && (
          <Button variant="ghost" onClick={() => onChange({ ...EMPTY_FILTER })}>
            <X size={13} aria-hidden="true" />
            {S.search.clearFilters}
          </Button>
        )}
      </div>

      <label className="flex flex-col gap-1 pb-3">
        <span className="text-sm text-text-muted">{S.search.term}</span>
        <input
          ref={searchRef}
          value={filter.term}
          onChange={(event) => set({ term: event.target.value })}
          placeholder={S.contacts.searchPlaceholder}
          className="h-control rounded border border-border bg-bg px-3 text-base
                     outline-none focus:border-primary"
        />
        <span className="text-xs text-text-muted">{S.search.termHint}</span>
      </label>

      {/* «مرحله اشتراک» and «نوع اشتراک» were removed from this panel on
          request. The CONCEPTS are untouched — the pipeline still drives the
          analytics funnel, the messaging automation rules and the profile's
          own status history — they are simply not how this deployment filters
          its contacts. `lib/filters.js` dropped the two keys with them, so a
          stale link cannot apply a filter the panel can no longer show. */}
      <Section title={S.search.kind} defaultOpen={false}>
        {KINDS.map((kind) => (
          <CheckOption
            key={kind}
            label={CONTACT_KIND_LABELS[kind]}
            checked={filter.kinds.includes(kind)}
            onChange={() => toggle('kinds', kind)}
          />
        ))}
      </Section>

      <Section title={S.search.tags}>
        <div className="flex gap-2 px-2 pb-1 text-xs">
          {['ANY', 'ALL'].map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => set({ tagMatch: mode })}
              aria-pressed={filter.tagMatch === mode}
              className={`rounded-full border px-3 py-1 ${
                filter.tagMatch === mode
                  ? 'border-primary bg-primary text-primary-contrast'
                  : 'border-border text-text-muted'
              }`}
            >
              {mode === 'ANY' ? S.search.tagMatchAny : S.search.tagMatchAll}
            </button>
          ))}
        </div>
        {/* Assessment tags have their own section below, so a tag appears
            in exactly one place — the same split the list's «ارزیابی» and
            «برچسب‌ها» columns make. */}
        {tags
          .filter((tag) => !tag.isAssessment)
          .map((tag) => (
            <CheckOption
              key={tag.id}
              label={tag.name}
              count={counts.get(`TAG:${tag.id}`) ?? 0}
              checked={filter.tagIds.includes(tag.id)}
              onChange={() => toggle('tagIds', tag.id)}
            />
          ))}
      </Section>

      {/* «ارزیابی» — the SAME tags, read through their `isAssessment` flag.
          Nothing here names «مشتاق»: a deployment that renames all four gets
          renamed options, and one that adds a fifth gets a fifth option, from
          the same source the contact profile tags from.

          Its own filter key rather than `tagIds`, because tags OR under
          `tagMatch` and this has to AND with them — «ارزیابی=مشتاق» plus
          «برچسب=بدهکار» means both, like every other pair on this panel. */}
      {assessments.length > 0 && (
        <Section title={S.search.assessment}>
          {assessments.map((tag) => (
            <CheckOption
              key={tag.id}
              label={tag.name}
              count={counts.get(`TAG:${tag.id}`) ?? 0}
              checked={filter.assessmentTagIds.includes(tag.id)}
              onChange={() => toggle('assessmentTagIds', tag.id)}
            />
          ))}
        </Section>
      )}

      <Section title={S.search.fieldOfWork} defaultOpen={false}>
        {(lookups.FIELD_OF_WORK ?? []).map((item) => (
          <CheckOption
            key={item.id}
            label={item.labelFa}
            count={counts.get(`FIELD_OF_WORK:${item.id}`) ?? 0}
            checked={filter.fieldOfWorkIds.includes(item.id)}
            onChange={() => toggle('fieldOfWorkIds', item.id)}
          />
        ))}
      </Section>

      <Section title={S.search.specialty} defaultOpen={false}>
        <p className="px-2 pb-1 text-xs text-text-muted">{S.search.specialtyHint}</p>
        {(lookups.SPECIALTY ?? []).map((item) => (
          <CheckOption
            key={item.id}
            label={item.labelFa}
            count={counts.get(`SPECIALTY:${item.id}`) ?? 0}
            checked={filter.specialtyIds.includes(item.id)}
            onChange={() => toggle('specialtyIds', item.id)}
            indent={item.parentId ? 1 : 0}
          />
        ))}
      </Section>

      <Section title={S.search.products} defaultOpen={false}>
        {(lookups.PRODUCT ?? []).map((item) => (
          <CheckOption
            key={item.id}
            label={item.labelFa}
            checked={filter.productIds.includes(item.id)}
            onChange={() => toggle('productIds', item.id)}
          />
        ))}
      </Section>

      {VOCABULARY_SECTIONS.map(([kind, key, title]) => (
        <Section key={kind} title={S.search[title]} defaultOpen={false}>
          {(lookups[kind] ?? []).map((item) => (
            <CheckOption
              key={item.id}
              label={item.labelFa}
              checked={(filter[key] ?? []).includes(item.id)}
              onChange={() => toggle(key, item.id)}
            />
          ))}
        </Section>
      ))}

      <Section title={S.search.gender} defaultOpen={false}>
        {GENDERS.map((gender) => (
          <CheckOption
            key={gender}
            label={GENDER_LABELS[gender]}
            checked={filter.genders.includes(gender)}
            onChange={() => toggle('genders', gender)}
          />
        ))}
      </Section>

      {/* TRI-STATE, so it is radio buttons and not a checkbox: «همه» is a real
          answer and the default, and a checkbox could only say two of the
          three things. */}
      <Section title={S.search.gold} defaultOpen={false}>
        {[
          [null, S.search.goldAny],
          [true, S.search.goldOnly],
          [false, S.search.goldRegular],
        ].map(([value, label]) => (
          <label
            key={String(value)}
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm
                       hover:bg-surface-hover"
          >
            <input
              type="radio"
              name="gold"
              className="accent-primary"
              checked={filter.isGold === value}
              onChange={() => set({ isGold: value })}
            />
            <span className="min-w-0 flex-1 truncate">{label}</span>
          </label>
        ))}
      </Section>

      <Section title={S.search.referrer} defaultOpen={false}>
        <ReferrerFilter
          value={filter.referrerContactId}
          onChange={(id) => set({ referrerContactId: id })}
        />
      </Section>

      <Section title={S.search.lastInteractionRange} defaultOpen={false}>
        <DateRange
          from={filter.lastInteractionFrom}
          to={filter.lastInteractionTo}
          onFrom={(v) => set({ lastInteractionFrom: v })}
          onTo={(v) => set({ lastInteractionTo: v })}
        />
        <label className="flex flex-col gap-1 px-2 pt-2 text-sm">
          <span className="text-text-muted">{S.search.noInteractionDays}</span>
          <input
            type="number"
            min="1"
            value={filter.hasNoInteractionSinceDays ?? ''}
            onChange={(event) =>
              set({
                hasNoInteractionSinceDays: event.target.value
                  ? Number(event.target.value)
                  : null,
              })
            }
            className="h-control rounded border border-border bg-bg px-3 tabular-nums
                       outline-none focus:border-primary"
          />
        </label>
      </Section>

      <Section title={S.search.createdRange} defaultOpen={false}>
        <DateRange
          from={filter.createdFrom}
          to={filter.createdTo}
          onFrom={(v) => set({ createdFrom: v })}
          onTo={(v) => set({ createdTo: v })}
        />
      </Section>
    </aside>
  );
}

/**
 * A date range.
 *
 * `<input type="date">` shows the BROWSER's calendar, which under a Persian
 * locale is Jalali and under an English one is Gregorian — so what the user
 * sees depends on their machine rather than on the app. That is a known gap
 * (ui.md): the honest fix is a Jalali date picker component, and building one
 * is not what Step 5 is for. The value stored is always ISO, so nothing
 * downstream is ambiguous.
 */
function DateRange({ from, to, onFrom, onTo }) {
  return (
    <div className="flex gap-2 px-2">
      <label className="flex flex-1 flex-col gap-1 text-sm">
        <span className="text-text-muted">{S.app.from}</span>
        <input
          type="date"
          value={from ?? ''}
          onChange={(event) => onFrom(event.target.value)}
          className="h-control rounded border border-border bg-bg px-2 text-sm
                     outline-none focus:border-primary"
        />
      </label>
      <label className="flex flex-1 flex-col gap-1 text-sm">
        <span className="text-text-muted">{S.app.to}</span>
        <input
          type="date"
          value={to ?? ''}
          onChange={(event) => onTo(event.target.value)}
          className="h-control rounded border border-border bg-bg px-2 text-sm
                     outline-none focus:border-primary"
        />
      </label>
    </div>
  );
}
